import {
  APP_INITIALIZER,
  ApplicationConfig,
  LOCALE_ID,
  importProvidersFrom,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { DatePipe } from '@angular/common';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { TranslocoDatePipe } from '../../../../libs/shared/src/pipes/transloco-date.pipe';
import {
  APP_MAT_DATE_FORMATS,
  AppDateAdapter,
} from '../../../../libs/shared/src/providers/app-date-adapter';
import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';

import { appRoutes } from './app.routes';
import { environment } from '../environments/environment';

import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';

import { provideTransloco } from '@jsverse/transloco';
import { HttpLoader } from '../../../../libs/shared/src/functions/http-loader';
import { CustomRouteReuseStrategy } from '../../../../libs/shared/src/shared/class/routeStrategy.class';

import { LayoutModule } from '@angular/cdk/layout';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialogModule } from '@angular/material/dialog';

import { ApiModule, Configuration, ClinicPremiumBillingsService } from 'libs/portal-data/src';

import { registerLocaleData } from '@angular/common';
import localeSrLatn from '@angular/common/locales/sr-Latn';
import localeEnGB from '@angular/common/locales/en-GB';
import localeDa from '@angular/common/locales/da';
import localeNb from '@angular/common/locales/nb';
import localeIt from '@angular/common/locales/it';
import localeDe from '@angular/common/locales/de';
import localeFr from '@angular/common/locales/fr';
import localeHr from '@angular/common/locales/hr';
import localeEs from '@angular/common/locales/es';
import localeSv from '@angular/common/locales/sv';
import localeRu from '@angular/common/locales/ru';
import localeAr from '@angular/common/locales/ar';
import { appInit } from '../../../../libs/shared/src/functions/appInitLang';

import { ServerHttpInterceptor } from './shared/interceptors/server-http.interceptor';
import { authHeaderInterceptor } from './shared/interceptors/auth-header.interceptor.fn';
import { refreshTokenInterceptor } from './shared/interceptors/refresh-token.interceptor.fn';
import { TokenRefreshService } from './shared/services/token-refresh.service';
import { provideTranslocoMatDatepickerIntl } from '../../../../libs/shared/src/providers/transloco-mat-datepicker-intl';

registerLocaleData(localeEnGB, 'en-GB');
registerLocaleData(localeDa, 'da-DK');
registerLocaleData(localeNb, 'nb-NO');
registerLocaleData(localeSrLatn, 'sr-Latn');
registerLocaleData(localeIt, 'it-IT');
registerLocaleData(localeDe, 'de-DE');
registerLocaleData(localeFr, 'fr-FR');
registerLocaleData(localeHr, 'hr-HR');
registerLocaleData(localeEs, 'es-ES');
registerLocaleData(localeSv, 'sv-SE');
registerLocaleData(localeRu, 'ru-RU');
registerLocaleData(localeAr, 'ar-SA');
registerLocaleData(localeEnGB, 'en');
registerLocaleData(localeDa, 'da');
registerLocaleData(localeNb, 'nb');
registerLocaleData(localeSrLatn, 'sr');
registerLocaleData(localeIt, 'it');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeHr, 'hr');
registerLocaleData(localeEs, 'es');
registerLocaleData(localeSv, 'sv');
registerLocaleData(localeRu, 'ru');
registerLocaleData(localeAr, 'ar');

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: appInit() },

    // Angular 21: Zoneless change detection for better performance
    provideZonelessChangeDetection(),

    provideRouter(appRoutes),
    provideAnimations(),
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: APP_MAT_DATE_FORMATS },
    TranslocoDatePipe,
    { provide: DatePipe, useExisting: TranslocoDatePipe },
    ...provideTranslocoMatDatepickerIntl(),

    // Refresh token on startup — runs before any route guard.
    // Ensures access_token is in memory before the app renders protected routes.
    {
      provide: APP_INITIALIZER,
      useFactory: (tokenRefresh: TokenRefreshService) => () =>
        tokenRefresh.startupRefresh(),
      deps: [TokenRefreshService],
      multi: true,
    },

    // Optimizovan redosled interceptora:
    provideHttpClient(
      withInterceptors([
        authHeaderInterceptor, // 1) Dodaje Authorization header
        refreshTokenInterceptor, // 2) Hvata 401 i refresh-uje token
      ]),
      withInterceptorsFromDi(), // 3) Uključuje klasne interceptore (ServerHttpInterceptor)
    ),

    // Klasni interceptor za error handling i snackbars (izvršava se poslednji)
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServerHttpInterceptor,
      multi: true,
    },

    importProvidersFrom(
      ApiModule.forRoot(
        () => new Configuration({ basePath: environment.apiUrl }),
      ),
      LayoutModule,
      MatMenuModule,
      MatDialogModule,
    ),

    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },

    provideTransloco({
      config: {
        availableLangs: [
          'ar-SA',
          'da-DK',
          'de-DE',
          'en-GB',
          'es-ES',
          'fr-FR',
          'hr-HR',
          'it-IT',
          'nb-NO',
          'ru-RU',
          'sr-Latn',
          'sv-SE',
        ],
        defaultLang: appInit(),
        reRenderOnLangChange: true,
      },
      loader: HttpLoader,
    }),

    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 4000 } },

    ClinicPremiumBillingsService,
  ],
};
